import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Globe2, Calendar, Award, MapPin, Trophy, Users, ShieldCheck, Zap, Heart, Quote, Star, Sparkles } from "lucide-react";
import DestinationCard from "@/components/DestinationCard";
import Seo from "@/components/Seo";
import { listDestinations } from "@/lib/api";
import { HERO_BG_URL, MICE_IMG } from "@/lib/media";
import { BLOG_POSTS } from "@/lib/blog-posts";

const HOME_BREADCRUMB = [{ name: "Home", path: "/" }];

const DMC_DESTINATIONS = [
  { to: "/destinations/asia-dmc", name: "Asia DMC Hub", note: "B2B regional partner · 11 countries", featured: true },
  { to: "/dubai-dmc", name: "Dubai", note: "GCC HQ · MICE & corporate" },
  { to: "/singapore-dmc", name: "Singapore", note: "MICE capital of Asia" },
  { to: "/malaysia-dmc", name: "Malaysia", note: "KL · Penang · Langkawi" },
  { to: "/thailand-dmc", name: "Thailand", note: "Bangkok · Phuket · Chiang Mai" },
  { to: "/hong-kong-dmc", name: "Hong Kong", note: "Harbour events & galas" },
  { to: "/vietnam-dmc", name: "Vietnam", note: "Hanoi · HCMC · Ha Long Bay" },
  { to: "/philippines-dmc", name: "Philippines", note: "Manila · Cebu · Palawan" },
  { to: "/cambodia-dmc", name: "Cambodia", note: "Siem Reap · Phnom Penh" },
  { to: "/bali-dmc", name: "Bali", note: "Indonesia · Nusa Dua · Ubud" },
  { to: "/laos-dmc", name: "Laos", note: "Luang Prabang · Vientiane" },
  { to: "/japan-dmc", name: "Japan", note: "Tokyo · Kyoto · Osaka" },
];

const SERVICES = [
  {
    icon: Trophy,
    to: "/mice-services",
    title: "MICE Services",
    copy: "Full conference production — venue, AV, F&B, gala, team-building. 70% of our volume.",
  },
  {
    icon: Users,
    to: "/corporate-travel",
    title: "Corporate Travel",
    copy: "Dedicated account managers, 48-hour proposals, 24/7 ground ops for groups of 10–1,000+.",
  },
  {
    icon: Award,
    to: "/incentive-travel",
    title: "Incentive Travel",
    copy: "Bespoke programmes that reward and motivate top performers across 11 destinations.",
  },
];

const FALLBACK_DESTS = [
  { slug: "dubai", name: "Dubai", country: "United Arab Emirates", tagline: "Where Ambition Meets the Desert" },
  { slug: "singapore", name: "Singapore", country: "Singapore", tagline: "The Lion City Roars" },
  { slug: "malaysia", name: "Malaysia", country: "Malaysia", tagline: "Truly Asia, Truly Yours" },
  { slug: "thailand", name: "Thailand", country: "Thailand", tagline: "Land of Smiles" },
  { slug: "hong-kong", name: "Hong Kong", country: "Hong Kong SAR", tagline: "Asia's World City" },
  { slug: "philippines", name: "Philippines", country: "Philippines", tagline: "7,641 Islands of Discovery" },
  { slug: "vietnam", name: "Vietnam", country: "Vietnam", tagline: "Timeless Charm" },
];

const WHY_PILLARS = [
  {
    icon: ShieldCheck,
    label: "Single accountable team",
    note: "One account manager. One ops lead. One contract. Across all 11 destinations.",
  },
  {
    icon: Zap,
    label: "48-hour proposals",
    note: "Costed, themed proposals back within 48 hours — even for 1,000+ pax briefs.",
  },
  {
    icon: Globe2,
    label: "11 Asia & ME destinations",
    note: "Dubai HQ + 10 operating teams across SE Asia, India and East Asia.",
  },
  {
    icon: Heart,
    label: "20 years of relationships",
    note: "Senior-team hotel & venue access most online channels can't unlock.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Trawelldmc delivered a 1,800-pax conference in Dubai with zero missed transfers and a 96/100 NPS. Their senior team unlocked inventory three other DMCs told us was unavailable.",
    name: "Director, Global Events",
    title: "Fortune 500 Enterprise SaaS",
    location: "Dubai · 5-day Conference · 1,800 pax",
  },
  {
    quote:
      "A 60-day Singapore incentive on F1 weekend felt impossible. They booked a 380-key buyout at JW Marriott, secured a Flower Dome gala, and delivered the highest-NPS incentive trip we've ever run.",
    name: "VP Sales Operations",
    title: "Asia Insurance Brand",
    location: "Singapore · 4-day Incentive · 360 pax",
  },
  {
    quote:
      "Our channel-partner launch in Bali generated 4× the engagement of the prior year — a beachfront drone-light gala that partners still re-share three months later. Trawelldmc is now our 3-year channel-event partner.",
    name: "Head of Channel Marketing",
    title: "Consumer Electronics Brand",
    location: "Bali · 4-day Product Launch · 350 pax",
  },
];

const INDUSTRIES = [
  "Enterprise SaaS",
  "Banking & Insurance",
  "Pharmaceuticals",
  "Consumer Electronics",
  "Automotive",
  "FMCG",
  "Telecommunications",
  "Real Estate",
];


export default function Home() {
  const [destinations, setDestinations] = useState(FALLBACK_DESTS);

  useEffect(() => {
    listDestinations().then(setDestinations).catch(() => {});
    // listDestinations is a stable module-level helper; setDestinations is a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Asia DMC & B2B Destination Management Company | Trawelldmc"
        description="The B2B Asia DMC trusted by corporate travel planners, MICE organizers and outbound travel agents. Single MICE travel partner across Dubai, Singapore, Malaysia, Thailand, Hong Kong, Vietnam & Philippines. 20+ years. 140,000+ travellers."
        breadcrumb={HOME_BREADCRUMB}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 hero-panorama grain-overlay"
          style={{ backgroundImage: `url(${HERO_BG_URL})` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(253,252,248,0.94) 0%, rgba(253,252,248,0.78) 40%, rgba(253,252,248,0.15) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-28 pb-32 md:pt-36 md:pb-48">
          <div className="max-w-3xl fade-up">
            <div className="overline mb-6">B2B DMC · 11 Asian Markets · Dubai HQ</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-[#0B192C] leading-[1.02]">
              Big MICE briefs.
              <br />
              Eleven Asian markets.
              <br />
              <span className="text-[#FF5238]">One DMC.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              Trawelldmc is the Dubai-headquartered B2B Destination Management
              Company built for MICE planners, corporate event teams and
              outbound agents. 20+ years. 140,000+ travellers. Eleven Asian
              destinations — operated end-to-end by one accountable team.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/quote" className="btn-primary" data-testid="hero-ai-quote-btn">
                Get an AI first quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/destinations/asia-dmc" className="btn-secondary" data-testid="hero-destinations-btn">
                Explore Asia DMC hub
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative bg-[#0B192C] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ["20+", "Years", "Of DMC craftsmanship"],
              ["140,000+", "Pax Managed", "Across leisure & MICE"],
              ["11", "Destinations", "Asia & Middle East"],
              ["24/7", "On-Ground", "Global concierge"],
            ].map(([v, l, s]) => (
              <div key={l} className="border-l-2 border-[#FF5238] pl-5">
                <div className="font-heading text-4xl md:text-5xl font-light tracking-tight">{v}</div>
                <div className="mt-2 text-xs font-bold tracking-[0.25em] uppercase text-[#FF5238]">{l}</div>
                <div className="text-sm text-slate-300 mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TRAWELLDMC — 4 pillars */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24" data-testid="why-pillars">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="overline mb-4">Why Trawelldmc</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              The B2B Asia DMC <br />
              corporate buyers <span className="text-[#FF5238]">rebook.</span>
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base leading-relaxed">
            Four reasons our 140,000+ travellers come from clients who chose
            us once, then chose us again.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_PILLARS.map((p) => (
            <div
              key={p.label}
              className="card-flat p-7 hover:border-[#FF5238] transition-colors"
              data-testid={`pillar-${p.label.toLowerCase().replace(/ /g, "-")}`}
            >
              <p.icon className="h-9 w-9 text-[#FF5238]" />
              <div className="font-heading text-lg font-medium text-[#0B192C] mt-5">
                {p.label}
              </div>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{p.note}</p>
            </div>
          ))}
        </div>

        {/* Industries served strip */}
        <div className="mt-16 pt-10 border-t border-slate-200">
          <div className="overline mb-5 text-center">Industries we serve</div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {INDUSTRIES.map((ind, i) => (
              <div key={ind} className="flex items-center gap-3">
                <span className="font-heading text-sm md:text-base text-[#0B192C] whitespace-nowrap">
                  {ind}
                </span>
                {i < INDUSTRIES.length - 1 && (
                  <span className="h-1 w-1 rounded-full bg-[#FF5238]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="destinations-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="overline mb-4">Our Destinations</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Eleven destinations. <br />One trusted operator.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base leading-relaxed">
            Each destination is backed by a local ground team, live-availability hotel contracts, and
            over two decades of relationships with tour operators, DMCs, and venue partners.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="destinations-bento">
          {destinations[0] && <DestinationCard destination={destinations[0]} span="tall" />}
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {destinations.slice(1, 7).map((d) => (
              <DestinationCard key={d.slug} destination={d} />
            ))}
          </div>
          {destinations[7] && (
            <div className="md:col-span-12">
              <DestinationCard destination={destinations[7]} span="wide" />
            </div>
          )}
          {destinations.length > 8 && (
            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {destinations.slice(8, 11).map((d) => (
                <DestinationCard key={d.slug} destination={d} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MICE focus */}
      <section className="bg-[#0B192C] text-white" data-testid="mice-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="overline mb-4">MICE Specialists</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-tight">
              Meetings. Incentives. <br />
              Conferences. <span className="text-[#FF5238]">Events.</span>
            </h2>
            <p className="text-slate-300 mt-6 text-base leading-relaxed max-w-xl">
              70% of our volume is MICE. From a 40-pax board offsite to a 2,500-pax national dealer
              conference — we source venues, negotiate rates, build creative production, and run the
              floor. Dubai, Singapore, Bangkok, KL, Ho Chi Minh, Hong Kong, Manila.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6">
              {[
                [Briefcase, "Corporate Offsites"],
                [Award, "Dealer Incentives"],
                [Calendar, "Global Conferences"],
                [Globe2, "Product Launches"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-[#FF5238]" />
                  <span className="font-heading text-sm tracking-wide">{label}</span>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link to="/mice" className="btn-primary" data-testid="mice-learn-more-btn">
                Explore MICE capabilities <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative overflow-hidden">
              <img
                src={MICE_IMG}
                alt="Trawelldmc MICE event production case study"
                width="1600"
                height="1000"
                loading="lazy"
                decoding="async"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur p-5 max-w-sm">
                <div className="overline !text-[#FF5238] mb-2">Case Study</div>
                <div className="font-heading text-lg font-semibold text-[#0B192C] leading-snug">
                  1,800-pax Global Sales Conference · Dubai · 5 days · 11 hotels
                </div>
                <div className="text-xs text-slate-500 mt-2">Executed April 2024</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR DMC DESTINATIONS — SEO internal links */}
      <section className="bg-[#FDFCF8] border-y border-slate-200" data-testid="home-dmc-destinations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="overline mb-4">Our Destinations</div>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
                Eleven cities. <br />Eleven dedicated DMC teams.
              </h2>
            </div>
            <p className="text-slate-600 max-w-md text-base leading-relaxed">
              Each destination is operated by a local on-ground team. Explore
              destination-specific venues, services and FAQs below.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {DMC_DESTINATIONS.map((d) => (
              <Link
                key={d.to}
                to={d.to}
                className="card-flat p-6 group hover:border-[#FF5238] transition-colors"
                data-testid={`home-dmc-${d.name.toLowerCase().replace(/ /g, "-")}`}
              >
                <MapPin className="h-5 w-5 text-[#FF5238]" />
                <div className="font-heading text-xl font-medium text-[#0B192C] mt-4 group-hover:text-[#FF5238] transition-colors">
                  {d.name}
                </div>
                <div className="text-sm text-slate-500 mt-1">{d.note}</div>
                <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-4 group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            ))}
            <Link
              to="/destinations"
              className="card-flat p-6 group hover:border-[#FF5238] transition-colors bg-[#0B192C] text-white border-[#0B192C]"
              data-testid="home-dmc-all"
            >
              <Globe2 className="h-5 w-5 text-[#FF5238]" />
              <div className="font-heading text-xl font-medium mt-4">All destinations</div>
              <div className="text-sm text-white/60 mt-1">Browse packages & itineraries</div>
              <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-4 group-hover:gap-2 transition-all">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="home-services-section">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">Our Services</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Three disciplines. <br />One accountable team.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="card-flat p-8 group hover:border-[#FF5238] transition-colors"
              data-testid={`home-service-${s.title.toLowerCase().replace(/ /g, "-")}`}
            >
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-2xl font-medium tracking-tight text-[#0B192C] mt-5 group-hover:text-[#FF5238] transition-colors">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
              <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-6 group-hover:gap-2 transition-all">
                Explore {s.title.split(" ")[0].toLowerCase()} services <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="testimonials-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="overline mb-4">Client Voices</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              What corporate <br />
              planners say.
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-[#FF5238] text-[#FF5238]" />
              ))}
            </div>
            <span>4.9 / 5 across MICE briefs operated 2023–2025</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="card-flat p-8 flex flex-col h-full"
              data-testid={`testimonial-${i}`}
            >
              <Quote className="h-8 w-8 text-[#FF5238]" />
              <blockquote className="font-heading text-lg text-[#0B192C] leading-snug mt-5 flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-7 pt-5 border-t border-slate-200">
                <div className="font-heading text-sm font-semibold text-[#0B192C]">
                  {t.name}
                </div>
                <div className="text-xs text-slate-500 mt-1">{t.title}</div>
                <div className="text-xs text-[#FF5238] mt-2">{t.location}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FEATURED CASE STUDY BANNER */}
      <section className="bg-[#0B192C] text-white" data-testid="featured-case-study">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline !text-[#FF5238] mb-5">Featured Case Study</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-tight">
              1,800-pax. <br />
              5 days. <br />
              <span className="text-[#FF5238]">11 hotels.</span>
            </h2>
            <p className="text-white/70 mt-6 text-lg leading-relaxed max-w-md">
              How Trawelldmc operated a Fortune-500 global sales conference in
              Dubai — zero missed transfers, 96/100 NPS, and a follow-on
              2,200-pax 2027 contract awarded 11 days after the event closed.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <div className="border-l-2 border-[#FF5238] pl-3">
                <div className="font-heading text-2xl font-light">96/100</div>
                <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Delegate NPS</div>
              </div>
              <div className="border-l-2 border-[#FF5238] pl-3">
                <div className="font-heading text-2xl font-light">0</div>
                <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Missed transfers</div>
              </div>
              <div className="border-l-2 border-[#FF5238] pl-3">
                <div className="font-heading text-2xl font-light">+22%</div>
                <div className="text-xs text-white/60 uppercase tracking-wider mt-1">Group rebook</div>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/blog/case-study-1800-pax-dubai-corporate-conference"
                className="btn-primary inline-flex"
                data-testid="featured-case-study-cta"
              >
                Read the case study <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/mice-case-studies-2025"
                className="btn-secondary inline-flex !bg-white/10 !border-white/20 !text-white hover:!bg-white/20"
                data-testid="home-lead-magnet-cta"
              >
                Get all 5 case studies (PDF)
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <img
              src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=webp&w=1600&q=80"
              alt="Trawelldmc 1,800-pax Dubai corporate conference case study"
              width="1600"
              height="1000"
              loading="lazy"
              decoding="async"
              className="w-full h-[460px] md:h-[520px] object-cover shadow-2xl"
            />
            <div className="absolute top-6 left-6 bg-[#FF5238] text-white px-4 py-2 inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-xs font-bold tracking-[0.18em] uppercase">
                Case Study · Dubai
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST INSIGHTS */}
      <section className="bg-[#FDFCF8] border-y border-slate-200" data-testid="home-blog-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="overline mb-4">Latest Insights</div>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
                From the Trawelldmc journal.
              </h2>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#FF5238] font-medium hover:gap-3 transition-all"
              data-testid="home-blog-all"
            >
              View all articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {BLOG_POSTS.slice(0, 4).map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group block"
                data-testid={`home-blog-${post.slug}`}
              >
                <article className="card-flat overflow-hidden h-full flex flex-col bg-white">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={post.image}
                      alt={`${post.title} — Trawelldmc Blog`}
                      width="1600"
                      height="1000"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="overline mb-3">{post.category}</div>
                    <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-[#0B192C] leading-tight group-hover:text-[#FF5238] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 mt-3 leading-relaxed text-sm flex-1">{post.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium">
                      Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI Quote promo */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="relative overflow-hidden bg-[#FDFCF8] border border-slate-200 p-10 md:p-16 grid grid-cols-1 md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3">
            <div className="overline mb-4">TrawellAI · First Quote in 2 min</div>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C]">
              Before you email three DMCs, <br />
              talk to ours for <span className="text-[#FF5238]">30 seconds.</span>
            </h2>
            <p className="text-slate-600 mt-5 max-w-xl leading-relaxed">
              Our AI concierge qualifies your brief and returns an indicative first quote — hotel
              class, transfers, sightseeing, and a total per pax — so you can start the conversation
              with real numbers, not a guess.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/quote" className="btn-primary" data-testid="ai-quote-section-btn">
                Start AI quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/inquiry" className="btn-ghost" data-testid="ai-quote-inquiry-btn">
                Or talk to a human →
              </Link>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="bg-white border border-slate-200 p-6 shadow-lg">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <div className="h-10 w-10 rounded-full bg-[#0B192C] flex items-center justify-center">
                  <span className="text-white text-sm font-heading font-semibold">AI</span>
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold">TrawellAI</div>
                  <div className="text-xs text-slate-500">Responding…</div>
                </div>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="bg-slate-50 p-3 rounded">&ldquo;5N Bangkok incentive for 120 pax, 4★, early May.&rdquo;</div>
                <div className="border border-slate-200 p-3">
                  <div className="font-heading font-semibold text-[#0B192C] mb-1">First Quote</div>
                  <div className="text-slate-600 text-xs leading-relaxed">
                    Bangkok 5N · 4★ hotel · transfers · 2 guided experiences · gala dinner venue sourcing.
                    <br />
                    <span className="text-[#FF5238] font-semibold">~$879 / pax</span> · Total ~$105,480 USD · indicative
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="border-y border-slate-200 overflow-hidden" data-testid="trust-band">
        <div className="marquee-track py-6">
          {[...Array(2)].flatMap((_, i) =>
            [
              "IATA Accredited",
              "2 Decades of Excellence",
              "Dubai · Singapore · Bangkok",
              "140,000+ Travelers Hosted",
              "MICE Specialists",
              "24/7 Global Concierge",
              "Trusted by Fortune 500",
              "Real-time Hotel Inventory",
            ].map((t, j) => (
              <div key={`${i}-${j}`} className="flex items-center gap-12 px-12">
                <span className="font-heading text-2xl font-light text-[#0B192C] whitespace-nowrap">{t}</span>
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF5238]" />
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
